import { useMemo, useState, type ReactNode } from 'react'
import {
  COOKING_SKILL_QUEST_PHASE_MESSAGE_KEYS,
  DEN_BOSCH_GATEWAY_OBJECTIVE_LABEL_KEYS,
  DEN_BOSCH_GATEWAY_QUEST_ID,
  DEN_BOSCH_GATEWAY_TARGET_VALUES,
  getQuestLogEntries,
  HIDEOUT_MOVE_QUEST_PHASE_MESSAGE_KEYS,
  STARTER_QUEST_PHASE_MESSAGE_KEYS,
  UTRECHT_GATEWAY_PHASE_COUNT,
  UTRECHT_GATEWAY_PHASE_DETAIL_KEYS,
  UTRECHT_GATEWAY_PHASE_MESSAGE_KEYS,
  UTRECHT_GATEWAY_POST_BROADCAST_LABEL_KEYS,
  UTRECHT_GATEWAY_POST_BROADCAST_TARGET_VALUES,
  UTRECHT_GATEWAY_QUEST_ID,
  VEHICLE_QUEST_PHASE_MESSAGE_KEYS,
  type QuestLogBody,
  type QuestLogCollapsedHint,
  type QuestLogEntry,
} from '../game/quests'
import {
  GatewayObjectiveRow,
  QuestCollapsibleCard,
  QuestTaskRow,
} from '../components/QuestCollapsibleCard'
import type { MessageKey } from '../i18n/messages'
import { useI18n } from '../i18n/useI18n'
import type { AchievementId, ProgressStats, QuestId } from '../game/types'
import { useGameStore } from '../store/useGameStore'
import { ACHIEVEMENTS } from '../game/achievements'

type AchievementProgressRule = {
  target: number
  current: (stats: ProgressStats) => number
}

const ACHIEVEMENT_PROGRESS_RULES = {
  first_100_food_collected: {
    target: 100,
    current: (stats) => stats.totalFoodCollected,
  },
  first_den_bosch_trip: {
    target: 1,
    current: (stats) => stats.denBoschTripsCompleted,
  },
  first_5_crafts: {
    target: 5,
    current: (stats) => stats.totalCraftsCompleted,
  },
  out_the_door: {
    target: 1,
    current: (stats) => stats.totalExpeditionsCompleted,
  },
  strength_in_numbers: {
    target: 3,
    current: (stats) => stats.survivorsRecruited,
  },
  full_house: {
    target: 6,
    current: (stats) => stats.survivorsRecruited,
  },
  on_four_wheels: {
    target: 1,
    current: (stats) => stats.vehiclesFound,
  },
  new_home: {
    target: 1,
    current: (stats) => stats.hideoutMoves,
  },
  signal_found: {
    target: 1,
    current: (stats) => (stats.radioBuilt ? 1 : 0),
  },
  water_bearer: {
    target: 100,
    current: (stats) => stats.totalWaterCollected,
  },
  workshop_regular: {
    target: 25,
    current: (stats) => stats.totalCraftsCompleted,
  },
  street_wise: {
    target: 10,
    current: (stats) => stats.denBoschTripsCompleted,
  },
  blooded: {
    target: 1,
    current: (stats) => stats.totalInjuries,
  },
} satisfies Record<AchievementId, AchievementProgressRule>

function achievementProgress(id: AchievementId, stats: ProgressStats, unlocked: boolean) {
  const rule = ACHIEVEMENT_PROGRESS_RULES[id]
  const target = Math.max(1, rule.target)
  const rawCurrent = unlocked ? target : rule.current(stats)
  const current = Math.min(Math.max(rawCurrent, 0), target)
  const percent = Math.round((current / target) * 100)

  return { current, target, percent }
}

function collapsedHintLabel(hint: QuestLogCollapsedHint, t: (k: MessageKey, p?: Record<string, string | number>) => string) {
  return hint.kind === 'message'
    ? t(hint.messageKey as MessageKey)
    : t('quests_collapsed_objectives', { done: hint.done, total: hint.total })
}

function gatewayActiveCurrents(body: Extract<QuestLogBody, { kind: 'gateway'; state: 'active' }>) {
  return [
    body.abandonedFarmTripsCompleted,
    body.foodLootValueAccumulated,
    body.waterLootValueAccumulated,
  ] as const
}

function utrechtGatewayPostBroadcastCurrents(
  body: Extract<QuestLogBody, { kind: 'utrecht_gateway'; state: 'active' }>,
) {
  return [
    body.nonTraderRecruitsSinceStart,
    body.scrapLootValuePostBroadcast,
    body.electronicsLootValuePostBroadcast,
  ] as const
}

function hasClaimableQuestReward(entry: QuestLogEntry) {
  return entry.body.state === 'completed' && !entry.body.rewardClaimed
}

function ClaimRewardButton({ onClaim }: { onClaim: () => void }) {
  return (
    <button
      type="button"
      onClick={onClaim}
      className="mt-3 w-full rounded-xl border border-emerald-600/60 bg-emerald-950/45 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-100 transition-colors hover:bg-emerald-950/70"
    >
      Claim reward
    </button>
  )
}

function renderQuestBody(
  entry: QuestLogEntry,
  t: (k: MessageKey, p?: Record<string, string | number>) => string,
  startQuest: (id: QuestId) => void,
  recheckVehicleQuestGarage: () => void,
  claimReward: (() => void) | null,
): ReactNode {
  const body = entry.body

  switch (body.kind) {
    case 'starter':
      if (body.state === 'inactive') {
        return (
          <>
            <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <p className="mt-3 text-xs text-zinc-500">{t('quest_starter_inactive')}</p>
          </>
        )
      }
      if (body.state === 'completed') {
        return (
          <>
            <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
              {STARTER_QUEST_PHASE_MESSAGE_KEYS.map((labelKey) => (
                <QuestTaskRow
                  key={labelKey}
                  checked
                  active={false}
                  label={t(labelKey as MessageKey)}
                />
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-emerald-900/45 bg-emerald-950/25 px-3 py-3">
              <p className="text-sm font-medium text-emerald-200/95">
                {t(body.completeTitleKey as MessageKey)}
              </p>
              <p className="mt-1 text-xs text-zinc-500">{t(body.completeSubtitleKey as MessageKey)}</p>
            </div>
            {!body.rewardClaimed && claimReward ? <ClaimRewardButton onClaim={claimReward} /> : null}
          </>
        )
      }
      return (
        <>
          <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
          <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
            {STARTER_QUEST_PHASE_MESSAGE_KEYS.map((labelKey, i) => (
              <QuestTaskRow
                key={labelKey}
                checked={i < body.phaseIndex}
                active={i === body.phaseIndex}
                label={t(labelKey as MessageKey)}
              />
            ))}
          </div>
        </>
      )

    case 'vehicle':
      if (body.state === 'completed') {
        return (
          <>
            <p className="text-sm leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
              {VEHICLE_QUEST_PHASE_MESSAGE_KEYS.map((labelKey) => (
                <QuestTaskRow
                  key={labelKey}
                  checked
                  active={false}
                  label={t(labelKey as MessageKey)}
                />
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-emerald-900/45 bg-emerald-950/25 px-3 py-3">
              <p className="text-sm font-medium text-emerald-200/95">
                {t(body.completeTitleKey as MessageKey)}
              </p>
              <p className="mt-1 text-xs text-zinc-500">{t(body.completeSubtitleKey as MessageKey)}</p>
            </div>
            {!body.rewardClaimed && claimReward ? <ClaimRewardButton onClaim={claimReward} /> : null}
          </>
        )
      }
      return (
        <>
          <p className="text-sm leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
          <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
            {VEHICLE_QUEST_PHASE_MESSAGE_KEYS.map((labelKey, i) => (
              <QuestTaskRow
                key={labelKey}
                checked={i === 0 ? body.phaseIndex > 0 : false}
                active={body.phaseIndex === i}
                label={
                  i === 0 && body.phaseIndex === 0 ? (
                    <span className="flex items-center justify-between gap-2">
                      <span>{t(labelKey as MessageKey)}</span>
                      <button
                        type="button"
                        onClick={recheckVehicleQuestGarage}
                        className="shrink-0 rounded border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition-colors"
                      >
                        {t('quest_garage_recheck')}
                      </button>
                    </span>
                  ) : (
                    t(labelKey as MessageKey)
                  )
                }
              />
            ))}
          </div>
        </>
      )

    case 'gateway':
      if (body.state === 'available') {
        return (
          <>
            <p className="text-sm leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 space-y-3">
              <p className="text-xs leading-relaxed text-zinc-500">{t(body.startHintKey as MessageKey)}</p>
              <button
                type="button"
                onClick={() => startQuest(DEN_BOSCH_GATEWAY_QUEST_ID)}
                className="w-full rounded-xl border border-sky-600/60 bg-sky-950/45 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-sky-100 transition-colors hover:bg-sky-950/70"
              >
                {t('quest_start_button')}
              </button>
            </div>
          </>
        )
      }
      if (body.state === 'completed') {
        return (
          <>
            <p className="text-sm leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 space-y-4 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-3 py-3">
              {DEN_BOSCH_GATEWAY_OBJECTIVE_LABEL_KEYS.map((labelKey, i) => {
                const need = DEN_BOSCH_GATEWAY_TARGET_VALUES[i] ?? 0
                return (
                  <GatewayObjectiveRow
                    key={labelKey}
                    label={t(labelKey as MessageKey)}
                    current={need}
                    need={need}
                    done
                  />
                )
              })}
            </div>
            <div className="mt-4 rounded-xl border border-emerald-900/45 bg-emerald-950/25 px-3 py-3">
              <p className="text-sm font-medium text-emerald-200/95">
                {t(body.completeTitleKey as MessageKey)}
              </p>
              <p className="mt-1 text-xs text-zinc-500">{t(body.completeSubtitleKey as MessageKey)}</p>
            </div>
            {!body.rewardClaimed && claimReward ? <ClaimRewardButton onClaim={claimReward} /> : null}
          </>
        )
      }
      {
        const currents = gatewayActiveCurrents(body)
        return (
          <>
            <p className="text-sm leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 space-y-4 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-3 py-3">
              {DEN_BOSCH_GATEWAY_OBJECTIVE_LABEL_KEYS.map((labelKey, i) => {
                const need = DEN_BOSCH_GATEWAY_TARGET_VALUES[i] ?? 0
                const cur = currents[i] ?? 0
                return (
                  <GatewayObjectiveRow
                    key={labelKey}
                    label={t(labelKey as MessageKey)}
                    current={cur}
                    need={need}
                    done={cur >= need}
                  />
                )
              })}
            </div>
          </>
        )
      }

    case 'cooking':
      if (body.state === 'completed') {
        return (
          <>
            <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
              {COOKING_SKILL_QUEST_PHASE_MESSAGE_KEYS.map((labelKey) => (
                <QuestTaskRow
                  key={labelKey}
                  checked
                  active={false}
                  label={t(labelKey as MessageKey)}
                />
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-emerald-900/45 bg-emerald-950/25 px-3 py-3">
              <p className="text-sm font-medium text-emerald-200/95">
                {t(body.completeTitleKey as MessageKey)}
              </p>
              <p className="mt-1 text-xs text-zinc-500">{t(body.completeSubtitleKey as MessageKey)}</p>
            </div>
            {!body.rewardClaimed && claimReward ? <ClaimRewardButton onClaim={claimReward} /> : null}
          </>
        )
      }
      return (
        <>
          <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
          <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
            {COOKING_SKILL_QUEST_PHASE_MESSAGE_KEYS.map((labelKey, i) => (
              <QuestTaskRow
                key={labelKey}
                checked={i < body.phaseIndex}
                active={i === body.phaseIndex}
                label={t(labelKey as MessageKey)}
              />
            ))}
          </div>
        </>
      )

    case 'hideout_move':
      if (body.state === 'completed') {
        return (
          <>
            <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
              {HIDEOUT_MOVE_QUEST_PHASE_MESSAGE_KEYS.map((labelKey) => (
                <QuestTaskRow
                  key={labelKey}
                  checked
                  active={false}
                  label={t(labelKey as MessageKey)}
                />
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-emerald-900/45 bg-emerald-950/25 px-3 py-3">
              <p className="text-sm font-medium text-emerald-200/95">
                {t(body.completeTitleKey as MessageKey)}
              </p>
              <p className="mt-1 text-xs text-zinc-500">{t(body.completeSubtitleKey as MessageKey)}</p>
            </div>
            {!body.rewardClaimed && claimReward ? <ClaimRewardButton onClaim={claimReward} /> : null}
          </>
        )
      }
      return (
        <>
          <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
          <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
            {HIDEOUT_MOVE_QUEST_PHASE_MESSAGE_KEYS.map((labelKey, i) => (
              <QuestTaskRow
                key={labelKey}
                checked={i < body.phaseIndex}
                active={i === body.phaseIndex}
                label={t(labelKey as MessageKey)}
              />
            ))}
          </div>
        </>
      )

    case 'utrecht_gateway':
      if (body.state === 'available') {
        return (
          <>
            <p className="text-sm leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 space-y-3">
              <p className="text-xs leading-relaxed text-zinc-500">{t(body.startHintKey as MessageKey)}</p>
              <button
                type="button"
                onClick={() => startQuest(UTRECHT_GATEWAY_QUEST_ID)}
                className="w-full rounded-xl border border-violet-600/60 bg-violet-950/45 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-violet-100 transition-colors hover:bg-violet-950/70"
              >
                {t('quest_start_button')}
              </button>
            </div>
          </>
        )
      }
      if (body.state === 'completed') {
        return (
          <>
            <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
              {UTRECHT_GATEWAY_PHASE_MESSAGE_KEYS.map((labelKey) => (
                <QuestTaskRow
                  key={labelKey}
                  checked
                  active={false}
                  label={t(labelKey as MessageKey)}
                />
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-emerald-900/45 bg-emerald-950/25 px-3 py-3">
              <p className="text-sm font-medium text-emerald-200/95">
                {t(body.completeTitleKey as MessageKey)}
              </p>
              <p className="mt-1 text-xs text-zinc-500">{t(body.completeSubtitleKey as MessageKey)}</p>
            </div>
            {!body.rewardClaimed && claimReward ? <ClaimRewardButton onClaim={claimReward} /> : null}
          </>
        )
      }
      {
        const currents = utrechtGatewayPostBroadcastCurrents(body)
        return (
          <>
            <p className="text-xs leading-relaxed text-zinc-400">{t(entry.bodyKey as MessageKey)}</p>
            <div className="mt-4 flex flex-col divide-y divide-zinc-800/80 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-2">
              {UTRECHT_GATEWAY_PHASE_MESSAGE_KEYS.map((labelKey, i) => (
                <QuestTaskRow
                  key={labelKey}
                  checked={i < body.phaseIndex}
                  active={i === body.phaseIndex}
                  label={t(labelKey as MessageKey)}
                />
              ))}
            </div>
            {body.phaseIndex < UTRECHT_GATEWAY_PHASE_COUNT ? (
              <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
                {t(UTRECHT_GATEWAY_PHASE_DETAIL_KEYS[body.phaseIndex] as MessageKey)}
              </p>
            ) : null}
            {body.phaseIndex === 3 ? (
              <div className="mt-4 space-y-4 rounded-xl border border-zinc-800/80 bg-zinc-950/40 px-3 py-3">
                {UTRECHT_GATEWAY_POST_BROADCAST_LABEL_KEYS.map((labelKey, i) => {
                  const need = UTRECHT_GATEWAY_POST_BROADCAST_TARGET_VALUES[i] ?? 0
                  const cur = currents[i] ?? 0
                  return (
                    <GatewayObjectiveRow
                      key={labelKey}
                      label={t(labelKey as MessageKey)}
                      current={cur}
                      need={need}
                      done={cur >= need}
                    />
                  )
                })}
              </div>
            ) : null}
          </>
        )
      }

    default: {
      const _exhaustive: never = body
      return _exhaustive
    }
  }
}

export function QuestsScreen() {
  const { t } = useI18n()
  const [journalTab, setJournalTab] = useState<'quests' | 'milestones'>('quests')

  const quests = useGameStore((s) => s.quests)
  const stats = useGameStore((s) => s.stats)
  const achievementsUnlocked = useGameStore((s) => s.achievementsUnlocked)
  const achievementsClaimed = useGameStore((s) => s.achievementsClaimed)
  const claimAchievement = useGameStore((s) => s.claimAchievement)
  const acknowledgeStarterQuestCompletion = useGameStore(
    (s) => s.acknowledgeStarterQuestCompletion,
  )
  const acknowledgeDenBoschGatewayCompletion = useGameStore(
    (s) => s.acknowledgeDenBoschGatewayCompletion,
  )
  const acknowledgeVehicleQuestCompletion = useGameStore(
    (s) => s.acknowledgeVehicleQuestCompletion,
  )
  const acknowledgeCookingQuestCompletion = useGameStore(
    (s) => s.acknowledgeCookingQuestCompletion,
  )
  const acknowledgeHideoutMoveQuestCompletion = useGameStore(
    (s) => s.acknowledgeHideoutMoveQuestCompletion,
  )
  const acknowledgeUtrechtGatewayCompletion = useGameStore(
    (s) => s.acknowledgeUtrechtGatewayCompletion,
  )
  const workstations = useGameStore((s) => s.workstations)
  const startQuest = useGameStore((s) => s.startQuest)
  const recheckVehicleQuestGarage = useGameStore((s) => s.recheckVehicleQuestGarage)

  const questLogEntries = useMemo(() => {
    const broadcastBuilt = workstations.broadcast_station?.built === true
    const entries = getQuestLogEntries(quests, { broadcastBuilt })
    return [...entries].sort((a, b) => {
      const aCompleted = a.body.state === 'completed' ? 1 : 0
      const bCompleted = b.body.state === 'completed' ? 1 : 0
      return aCompleted - bCompleted
    })
  }, [quests, workstations.broadcast_station?.built])

  const claimCallbacks = useMemo<Partial<Record<QuestId, (() => void) | null>>>(() => ({
    starter_survival: quests.starterSurvival.status === 'completed' && !quests.starterSurvival.acknowledged
      ? acknowledgeStarterQuestCompletion
      : null,
    den_bosch_gateway: quests.denBoschGateway.status === 'completed' && !quests.denBoschGateway.acknowledged
      ? acknowledgeDenBoschGatewayCompletion
      : null,
    vehicle_quest: quests.vehicleQuest.status === 'completed' && !quests.vehicleQuest.acknowledged
      ? acknowledgeVehicleQuestCompletion
      : null,
    cooking_skill_quest: quests.cookingSkillQuest.status === 'completed' && !quests.cookingSkillQuest.acknowledged
      ? acknowledgeCookingQuestCompletion
      : null,
    hideout_move_quest: quests.hideoutMoveQuest.status === 'completed' && !quests.hideoutMoveQuest.acknowledged
      ? acknowledgeHideoutMoveQuestCompletion
      : null,
    utrecht_gateway: quests.utrechtGateway.status === 'completed' && !quests.utrechtGateway.acknowledged
      ? acknowledgeUtrechtGatewayCompletion
      : null,
  }), [
    quests,
    acknowledgeStarterQuestCompletion,
    acknowledgeDenBoschGatewayCompletion,
    acknowledgeVehicleQuestCompletion,
    acknowledgeCookingQuestCompletion,
    acknowledgeHideoutMoveQuestCompletion,
    acknowledgeUtrechtGatewayCompletion,
  ])

  return (
    <div className="space-y-4">
      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          {t('nav_quests')}
        </h2>
      </section>

      <div className="flex gap-1 rounded-xl bg-zinc-800/60 p-1">
        <button
          type="button"
          onClick={() => setJournalTab('quests')}
          className={`flex-1 rounded-lg py-2 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
            journalTab === 'quests'
              ? 'bg-zinc-700 text-zinc-100 shadow-sm'
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          {t('journal_tab_quests')}
        </button>
        <button
          data-tutorial-id="milestones-tab"
          type="button"
          onClick={() => setJournalTab('milestones')}
          className={`relative flex-1 rounded-lg py-2 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
            journalTab === 'milestones'
              ? 'bg-zinc-700 text-zinc-100 shadow-sm'
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          {t('journal_tab_milestones')}
          {achievementsUnlocked.some((id) => !achievementsClaimed.includes(id)) && (
            <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-amber-500" />
          )}
        </button>
      </div>

      {journalTab === 'quests' && (
        <div data-tutorial-id="quest-list" className="space-y-3">
          {questLogEntries.map((entry) => {
            const rewardClaimable = hasClaimableQuestReward(entry)

            return (
              <QuestCollapsibleCard
                key={entry.id}
                category={entry.category}
                badgeLabel={t(entry.badgeKey as MessageKey)}
                title={t(entry.titleKey as MessageKey)}
                completed={entry.body.state === 'completed'}
                defaultExpanded={entry.defaultExpanded || rewardClaimable}
                collapsedHint={collapsedHintLabel(entry.collapsedHint, t)}
              >
                {renderQuestBody(entry, t, startQuest, recheckVehicleQuestGarage, claimCallbacks[entry.id] ?? null)}
              </QuestCollapsibleCard>
            )
          })}
        </div>
      )}

      {journalTab === 'milestones' && (
        <div className="space-y-3">
          {ACHIEVEMENTS.map((ach) => {
            const unlocked = achievementsUnlocked.includes(ach.id)
            const claimed = achievementsClaimed.includes(ach.id)
            const progress = achievementProgress(ach.id, stats, unlocked)
            return (
              <div
                key={ach.id}
                className={`rounded-2xl border p-4 transition-opacity ${
                  unlocked
                    ? 'border-emerald-800/50 bg-emerald-950/20 opacity-70'
                    : 'border-zinc-800/60 bg-zinc-900/70 opacity-100'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold ${
                        unlocked ? 'text-emerald-200' : 'text-zinc-400'
                      }`}
                    >
                      {t(`milestone_${ach.id}_title` as MessageKey)}
                    </p>
                    <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
                      {t(`milestone_${ach.id}_desc` as MessageKey)}
                    </p>
                    {!unlocked && (
                      <div className="mt-3">
                        <div className="mb-1 flex items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                          <span>{t('milestone_progress_label')}</span>
                          <span className="text-zinc-400">
                            {progress.current} / {progress.target}
                          </span>
                        </div>
                        <div
                          className="h-2 overflow-hidden rounded-full bg-zinc-800/80"
                          aria-label={t('milestone_progress_label')}
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={progress.target}
                          aria-valuenow={progress.current}
                        >
                          <div
                            className="h-full rounded-full bg-amber-500/75 transition-all"
                            style={{ width: `${progress.percent}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 flex items-center gap-0.5 text-xs font-semibold text-purple-400">
                    <span>◆</span>
                    <span>{ach.gemReward}</span>
                  </span>
                </div>
                {unlocked && !claimed && (
                  <button
                    type="button"
                    onClick={() => claimAchievement(ach.id)}
                    className="mt-3 w-full rounded-xl border border-emerald-600/60 bg-emerald-950/45 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-100 transition-colors hover:bg-emerald-950/70"
                  >
                    {t('milestone_claim_button')}
                  </button>
                )}
                {unlocked && claimed && (
                  <p className="mt-2 text-xs text-emerald-600/70 font-medium">
                    {t('milestone_claimed_label')}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
